import NewLookScoreInfo from "features/newMarketplace/NewLook/NewLookScoreInfo";
import Title from "common/Typography/Title";
import Text from "common/Typography/Text";
import {
  BacktestingScoreBreakdown,
  LanguageNameSpaces,
  MarketplaceSubGroup,
} from "@shared/types";
import Flex from "common/Boxes/Flex";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import Progress from "common/Progress/Progress";
import React from "react";
import styled, { css, useTheme } from "styled-components";
import { ChevronDown } from "lucide-react";
import ExpandCollapse from "common/Animations/ExpandCollapse";
import {
  ArrowRightOutlined,
  ProfitIcon,
  RiskIcon,
  ServiceIcon,
} from "common/Icons/Icons";
import useScore from "@shared/features/newMarketplace/hooks/useScore";
import { ScoreSection } from "@shared/features/newMarketplace/types";
import ScoreProgressChart from "features/newMarketplace/common/ScoreProgressChart";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { PAGE_SPINNER_MARGIN_FROM_TOP } from "common/constants";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import { SCORE_HELPER_PROPS } from "@shared/features/newMarketplace/constants";
import { TranslationKeys } from "@shared/common/i18n/types.ts";

interface ScoreInfoProps {
  group?: MarketplaceSubGroup;
  loading?: boolean;
  hideTitle?: boolean;
  inModal?: boolean;
  /** New look: the full breakdown (gauge, metrics open) for wide panels. */
  detailed?: boolean;
}

const ClassicScoreInfo = ({ group, loading, hideTitle }: ScoreInfoProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);
  const { isNewLook } = useTheme();
  const {
    theme,
    scoreBreakdown,
    sections,
    getSumSectionByKey,
    getFormattedValue,
    getSectionData,
    getSectionRowData,
    getProgressBarData,
  } = useScore({ group });

  const getDisplayedScore = (
    weightedScore: number,
    weight: number,
    isTitle?: boolean
  ) => {
    return (
      <Flex $alignItems="baseline">
        <WeightedScore $isTitle={isTitle}>{weightedScore}</WeightedScore>
        <Weight $isTitle={isTitle}>
          <span style={{ marginRight: 1 }}>/</span>
          {weight}
        </Weight>
      </Flex>
    );
  };

  const getCollapsedContent = (key: keyof BacktestingScoreBreakdown) => {
    return (
      <CollapsedContentBg>
        {Object.entries(scoreBreakdown?.[key] || {}).map(
          ([timeframe, scoreData]) => {
            return (
              <Flex key={`${key}_${timeframe}`}>
                <SecondaryText>
                  {t("score.last_days", {
                    timeframe,
                    value: getFormattedValue(key, scoreData?.value),
                  })}
                </SecondaryText>
                {getDisplayedScore(
                  Math.round(scoreData.weighted_score * 100),
                  Math.round(scoreData.weight * 100)
                )}
              </Flex>
            );
          }
        )}
      </CollapsedContentBg>
    );
  };

  const getProgressBar = (
    key: keyof BacktestingScoreBreakdown,
    section: ScoreSection
  ) => {
    const data = getProgressBarData(key, section);
    return (
      <div
        key={key}
        onClick={(e) => {
          if (data.isCollapsible) {
            e.stopPropagation();
            data.onClick();
          }
        }}
      >
        <Flex>
          <Flex $justifyContent="flex-start" style={{ flexWrap: "wrap" }}>
            <SecondaryText as="div" style={{ marginRight: 4 }}>
              {data.title}
              {data.helperProps && (
                <HelperTooltip
                  containerStyle={{ margin: "0 4px 0 6px" }}
                  {...data.helperProps}
                />
              )}
              {!data.isCollapsible && ":"}
            </SecondaryText>
            {data.isCollapsible ? (
              isNewLook ? (
                <NewLookChevron $expanded={data.expanded} size={16} />
              ) : (
                <Arrow
                  $expanded={data.expanded}
                  $size={20}
                  style={{ fill: theme.colors.secondaryText }}
                />
              )
            ) : (
              <SecondaryText>{data.value}</SecondaryText>
            )}
          </Flex>
          {getDisplayedScore(data.weightedScore, data.weight)}
        </Flex>
        <ExpandCollapse expanded={data.expanded ?? false}>
          {getCollapsedContent(key)}
        </ExpandCollapse>
        <Progress
          percent={data.progressPercent}
          strokeColor={data.progressColor}
          showInfo={false}
        />
      </div>
    );
  };

  const getProgressBarList = (
    section: ScoreSection,
    data: (keyof BacktestingScoreBreakdown)[]
  ) => {
    return data.map((key) => {
      return getProgressBar(key, section);
    });
  };

  const iconStyle = { width: 24, height: 24 };
  const SCORE_SECTION_TO_ICON = {
    [ScoreSection.PERFORMANCE]: <ProfitIcon style={iconStyle} />,
    [ScoreSection.RISK]: <RiskIcon style={iconStyle} />,
    [ScoreSection.SERVICE]: <ServiceIcon style={iconStyle} />,
  };

  const getSectionRow = (section: ScoreSection) => {
    const data = getSectionRowData(section);
    return (
      <SectionRow>
        <Flex>
          <SectionProgress>
            <Icon>{SCORE_SECTION_TO_ICON[section]}</Icon>
            <Progress
              type="circle"
              percent={data.progressPercent}
              strokeColor={data.progressColor}
              showInfo={false}
              size={52}
              strokeWidth={12}
              style={{ rotate: "180deg" }}
            />
          </SectionProgress>
          <Title level={3}>{section}</Title>
        </Flex>
        {getDisplayedScore(data.weightedScore, data.weight, true)}
      </SectionRow>
    );
  };

  const getSection = (section: ScoreSection, index: number) => {
    const data = getSectionData(section, index);
    return (
      <div key={section}>
        <SectionWrapper onClick={data.onClick} $isLast={data.isLastSection}>
          {getSectionRow(section)}
          {getProgressBarList(section, data.mainContent)}
          <ExpandCollapse expanded={data.expanded}>
            {getProgressBarList(section, data.collapsedContent)}
          </ExpandCollapse>
          {isNewLook ? (
            <NewLookSectionToggle>
              <NewLookChevron $expanded={data.expanded} size={18} />
            </NewLookSectionToggle>
          ) : (
            <Arrow $expanded={data.expanded} $addHover={true} />
          )}
        </SectionWrapper>
        {!data.isLastSection && <Border />}
      </div>
    );
  };

  return (
    <LoadingSpinnerOverlay
      isLoading={loading}
      hideContentWhileLoading={true}
      wrap={true}
      spinnerMarginTop={PAGE_SPINNER_MARGIN_FROM_TOP}
    >
      {!hideTitle && (
        <MainTitle>
          {t("score.title")}
          <HelperTooltip
            {...SCORE_HELPER_PROPS}
            description={t(SCORE_HELPER_PROPS.description)}
            title={t(SCORE_HELPER_PROPS.title)}
          />
        </MainTitle>
      )}
      <ScoreProgressChart getSumSectionByKey={getSumSectionByKey} />
      {sections.map((section, index) => {
        return getSection(section, index);
      })}
    </LoadingSpinnerOverlay>
  );
};

const ScoreInfo = (props: ScoreInfoProps) => {
  const { isNewLook } = useTheme();
  return isNewLook ? (
    <NewLookScoreInfo {...props} />
  ) : (
    <ClassicScoreInfo {...props} />
  );
};

export default ScoreInfo;

const MainTitle = styled(Title).attrs({
  level: 3,
  as: "div",
})`
  display: flex;
  margin-bottom: 12px;
  color: ${(p) => p.theme.colors.secondaryText};
  font-size: ${(p) => p.theme.typography.fontSizes.L};
  ${(p) =>
    p.theme.isNewLook &&
    `
    align-items: center;
    color: ${p.theme.colors.titleColor};
    font-size: 16px;
    font-weight: ${p.theme.typography.fontWeights.SEMI_BOLD};
  `}
`;
const SectionWrapper = styled.div<{ $isLast?: boolean }>`
  margin-bottom: ${(p) => (p.$isLast ? 0 : 10)}px;
  cursor: pointer;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* Slim, rounded bars; the metric rows breathe a little more. */
      .ant-progress-line {
        margin: 6px 0 12px;
      }
      .ant-progress-inner {
        background: ${p.theme.colors.lightestBorderGrey} !important;
      }
      .ant-progress-inner,
      .ant-progress-bg,
      .ant-progress-rail,
      .ant-progress-track {
        height: 6px !important;
        border-radius: 999px !important;
      }
    `}
`;
const NewLookChevron = styled(ChevronDown)<{ $expanded?: boolean }>`
  flex: none;
  color: ${(p) => p.theme.colors.secondaryText};
  stroke-width: 1.75;
  transform: rotate(${(p) => (p.$expanded ? 180 : 0)}deg);
  transition: transform 0.25s;
`;
const NewLookSectionToggle = styled.span`
  display: flex;
  justify-content: center;
  padding: 2px 0 6px;
  border-radius: 8px;
  transition: background-color 150ms;
  &:hover {
    background: ${(p) => p.theme.colors.cardLightBgColor};
  }
`;
const Border = styled.div`
  border-bottom: ${(p) =>
    p.theme.isNewLook ? p.theme.borders.lightest : p.theme.borders.light};
  margin-bottom: 18px;
`;
const Arrow = styled(ArrowRightOutlined)<{
  $expanded?: boolean;
  $size?: number;
  $addHover?: boolean;
}>`
  fill: ${(p) => p.theme.colors.primaryText};
  transform: rotate(${(p) => (p.$expanded ? 270 : 90)}deg);
  transition: transform 0.35s;
  width: ${(p) => p.$size ?? 24}px;
  height: ${(p) => p.$size ?? 24}px;
  ${(p) =>
    p.$addHover &&
    `
    &:hover {
      background-color: ${p.theme.colors.boxInCardColor};
      border-radius: 4px;
    }
  `};
`;
const CollapsedContentBg = styled.div`
  background-color: ${(p) =>
    p.theme.isNewLook
      ? p.theme.colors.cardLightBgColor
      : p.theme.colors.boxInCardColor};
  border-radius: ${(p) => (p.theme.isNewLook ? 12 : 8)}px;
  padding: 12px 20px;
  row-gap: 12px;
  display: grid;
  margin: 8px 0;
`;
const Icon = styled.div`
  position: absolute;
  left: calc(50% - 12px);
  top: calc(50% - 12px);
  width: 24px;
  height: 24px;
`;
const WeightedScore = styled(Title)<{ $isTitle?: boolean }>`
  margin-right: 4px;
  font-size: ${(p) =>
    p.$isTitle
      ? p.theme.typography.fontSizes.L
      : p.theme.typography.fontSizes.M};
  font-weight: ${(p) =>
    p.$isTitle
      ? p.theme.typography.fontWeights.SEMI_BOLD
      : p.theme.typography.fontWeights.MEDIUM};
`;
const Weight = styled(Text).attrs({ type: "secondary" })<{
  $isTitle?: boolean;
}>`
  font-size: ${(p) =>
    p.$isTitle
      ? p.theme.typography.fontSizes.M
      : p.theme.typography.fontSizes.S};
`;
const SecondaryText = styled(Text).attrs({ type: "secondary" })``;
const SectionRow = styled(Flex)`
  margin-bottom: 16px;
`;
const SectionProgress = styled.div`
  position: relative;
  margin-right: 16px;
`;
